import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    let scrollInterval: ReturnType<typeof setInterval>;
    let restartTimeout: ReturnType<typeof setTimeout>;
    let isActive = true;

    const startAutoScroll = () => {
      clearInterval(scrollInterval);
      if (!isActive) return;
      scrollInterval = setInterval(() => {
        if (!isActive) {
          clearInterval(scrollInterval);
          return;
        }
        window.scrollBy({ top: 1, behavior: 'auto' });
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
          clearInterval(scrollInterval);
        }
      }, 16);
    };

    const startScroll = setTimeout(() => {
      if (isActive) startAutoScroll();
    }, 1000);

    const stopScroll = () => {
      clearInterval(scrollInterval);
      clearTimeout(restartTimeout);
      restartTimeout = setTimeout(() => {
        if (isActive) startAutoScroll();
      }, 2000);
    };

    window.addEventListener('wheel', stopScroll);
    window.addEventListener('touchstart', stopScroll);
    window.addEventListener('keydown', stopScroll);

    return () => {
      isActive = false;
      clearTimeout(startScroll);
      clearTimeout(restartTimeout);
      clearInterval(scrollInterval);
      window.removeEventListener('wheel', stopScroll);
      window.removeEventListener('touchstart', stopScroll);
      window.removeEventListener('keydown', stopScroll);
    };
  }, [location]);

  const processSteps = [
    { step: '01', titleKey: 'aboutPage.process.step1.title', descKey: 'aboutPage.process.step1.desc', icon: 'ri-search-eye-line' },
    { step: '02', titleKey: 'aboutPage.process.step2.title', descKey: 'aboutPage.process.step2.desc', icon: 'ri-file-search-line' },
    { step: '03', titleKey: 'aboutPage.process.step3.title', descKey: 'aboutPage.process.step3.desc', icon: 'ri-team-line' },
    { step: '04', titleKey: 'aboutPage.process.step4.title', descKey: 'aboutPage.process.step4.desc', icon: 'ri-line-chart-line' },
  ];

  const values = [
    { titleKey: 'aboutPage.values.integrity', descKey: 'aboutPage.values.integrityDesc', icon: 'ri-shield-check-line' },
    { titleKey: 'aboutPage.values.longTerm', descKey: 'aboutPage.values.longTermDesc', icon: 'ri-time-line' },
    { titleKey: 'aboutPage.values.excellence', descKey: 'aboutPage.values.excellenceDesc', icon: 'ri-award-line' },
    { titleKey: 'aboutPage.values.innovation', descKey: 'aboutPage.values.innovationDesc', icon: 'ri-lightbulb-line' },
    { titleKey: 'aboutPage.values.collaboration', descKey: 'aboutPage.values.collaborationDesc', icon: 'ri-team-line' },
  ];

  const highlights = [
    {
      icon: 'ri-map-pin-line',
      titleKey: 'aboutPage.highlights.uaeBased',
      descKey: 'aboutPage.highlights.uaeBasedDesc',
    },
    {
      icon: 'ri-pie-chart-line',
      titleKey: 'aboutPage.highlights.multiSector',
      descKey: 'aboutPage.highlights.multiSectorDesc',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      titleKey: 'aboutPage.highlights.innovation',
      descKey: 'aboutPage.highlights.innovationDesc',
    },
    {
      icon: 'ri-line-chart-line',
      titleKey: 'aboutPage.highlights.longTerm',
      descKey: 'aboutPage.highlights.longTermDesc',
    },
  ];

  const whyPartner = [
    {
      icon: 'ri-time-line',
      titleKey: 'aboutPage.whyPartner.vision',
      descKey: 'aboutPage.whyPartner.visionDesc',
    },
    {
      icon: 'ri-settings-3-line',
      titleKey: 'aboutPage.whyPartner.support',
      descKey: 'aboutPage.whyPartner.supportDesc',
    },
    {
      icon: 'ri-book-open-line',
      titleKey: 'aboutPage.whyPartner.expertise',
      descKey: 'aboutPage.whyPartner.expertiseDesc',
    },
    {
      icon: 'ri-map-2-line',
      titleKey: 'aboutPage.whyPartner.market',
      descKey: 'aboutPage.whyPartner.marketDesc',
    },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-[#1A3A5C] to-[#0D2137] animate-gradient"></div>
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-gold/10 animate-gradient"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold/5 to-transparent animate-gradient" style={{ animationDelay: '2s' }}></div>
        </div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(201, 169, 98, 0.2) 0%, transparent 50%)`,
          }}></div>
        </div>
        
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className={`flex items-center gap-2 text-sm mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/" className="text-gray-400 hover:text-gold transition-colors cursor-pointer">{t('aboutPage.breadcrumb.home')}</Link>
            <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-500`}></i>
            <span className="text-gold">{t('aboutPage.breadcrumb.about')}</span>
          </div>
          
          <div className="max-w-3xl">
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-12 h-px bg-gold"></div>
              <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('aboutPage.hero.label')}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl text-white leading-tight mb-6">
              {t('aboutPage.hero.title1')} <span className="text-gold">{t('aboutPage.hero.title2')}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
              {t('about.description')}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">

          {/* Our Story */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center mb-16 md:mb-24">
            <div className={`relative ${isRTL ? 'lg:order-2' : ''}`}>
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/ourstory.webp"
                  alt="About ALEBEL"
                  className="w-full h-[400px] md:h-[600px] object-cover object-center"
                />
              </div>
              <div className={`absolute -bottom-6 ${isRTL ? '-left-6' : '-right-6'} bg-gold rounded-2xl p-4 md:p-6 shadow-xl`}>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-serif text-navy">2019</div>
                  <div className="text-navy/80 text-xs md:text-sm mt-1">{t('aboutPage.established')}</div>
                </div>
              </div>
            </div>

            <div className={`space-y-6 md:space-y-8 ${isRTL ? 'lg:order-1' : ''}`}>
              <h2 className="font-serif text-3xl md:text-4xl text-navy">
                {t('aboutPage.story.title1')} <span className="text-gold">{t('aboutPage.story.title2')}</span>
              </h2>
              <div className="space-y-4 md:space-y-6 text-gray-600 leading-relaxed text-sm md:text-base">
                <p>{t('aboutPage.story.p1')}</p>
                <p>{t('aboutPage.story.p2')}</p>
                <p>{t('aboutPage.story.p3')}</p>
              </div>
            </div>
          </div>

          {/* CEO Quote */}
          <div className="bg-gradient-to-br from-navy via-navy-light to-navy rounded-3xl p-8 md:p-12 lg:p-16 mb-16 md:mb-24 overflow-hidden relative">
            <div className="absolute inset-0 opacity-10">
              <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-96 h-96 bg-gold rounded-full blur-3xl`}></div>
            </div>
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className={`lg:col-span-4 ${isRTL ? 'lg:order-2' : ''}`}>
                <div className="relative">
                  <div className="w-36 h-36 md:w-48 md:h-48 mx-auto rounded-full overflow-hidden border-4 border-gold/30">
                    <div className="w-36 h-36 md:w-48 md:h-48 mx-auto rounded-full overflow-hidden border-4 border-gold/30 bg-white">
  <img
    src="/Gemini_Generated_Image_w6nuwpw6nuwpw6nu.png"
    alt="CEO"
    className="w-full h-full object-contain object-center scale-125"
  />
</div>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-6 py-2 rounded-full border border-white/20">
                    <span className="text-white text-sm font-medium">CEO</span>
                  </div>
                </div>
                <div className="text-center mt-6">
                  <div className="text-white font-semibold text-lg md:text-xl">{t('about.mhbm')}</div>
                  <div className="text-white font-semibold text-lg md:text-xl">{t('about.ceoName')}</div>
                  <div className="text-gold text-sm">{t('about.ceoTitle')}</div>
                </div>
              </div>
              <div className={`lg:col-span-8 space-y-6 md:space-y-8 ${isRTL ? 'lg:order-1' : ''}`}>
                <i className={`ri-double-quotes-${isRTL ? 'r' : 'l'} text-5xl md:text-6xl text-gold/30`}></i>
                <div className="space-y-4 md:space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-white/95 leading-relaxed font-light">{t('about.ceoQuote1')}</p>
                  <p className="text-lg md:text-xl lg:text-2xl text-white/95 leading-relaxed font-light">{t('about.ceoQuote2')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* How We Work */}
          <div className="mb-16 md:mb-24">
            <div className="text-center mb-12 md:mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-px bg-gold"></div>
                <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('aboutPage.process.label')}</span>
                <div className="w-12 h-px bg-gold"></div>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-navy">
                {t('aboutPage.process.title1')} <span className="text-gold">{t('aboutPage.process.title2')}</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {processSteps.map((item, index) => (
                <div key={index} className="group relative">
                  <div className="bg-white rounded-2xl p-6 md:p-8 h-full border border-gray-100 hover:border-gold/30 hover:shadow-xl transition-all duration-500">
                    <div className={`absolute -top-4 ${isRTL ? 'right-6 md:right-8' : 'left-6 md:left-8'} bg-gradient-to-r from-gold to-gold-dark text-navy text-sm font-bold px-4 py-1 rounded-full`}>
                      {item.step}
                    </div>
                    <div className="w-14 h-14 md:w-16 md:h-16 animated-gradient-box rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${item.icon} text-2xl md:text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-navy mb-4">{t(item.titleKey)}</h3>
                    <p className="text-gray-600 leading-relaxed text-xs md:text-sm">{t(item.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Portfolio Highlights */}
          <div className="mb-16 md:mb-24">
            <div className="text-center mb-12 md:mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-px bg-gold"></div>
                <span className="text-gold text-sm font-medium tracking-widest uppercase">
                  {isRTL ? 'مميزات المحفظة' : 'Portfolio Highlights'}
                </span>
                <div className="w-12 h-px bg-gold"></div>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-navy">
                {isRTL ? 'ما يميز' : 'What Makes'} <span className="text-gold">{isRTL ? 'الأبل' : 'ALEBEL'}</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {highlights.map((item, index) => (
                <div key={index} className="group">
                  <div className="bg-gradient-to-br from-navy/5 to-gold/5 rounded-2xl p-6 md:p-8 h-full border border-gold/10 hover:border-gold/30 hover:shadow-xl transition-all duration-500">
                    <div className="w-12 h-12 md:w-14 md:h-14 animated-gradient-box rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${item.icon} text-xl md:text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-navy mb-3">{t(item.titleKey)}</h3>
                    <p className="text-gray-600 leading-relaxed text-xs md:text-sm">{t(item.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Why Partner With ALEBEL */}
          <div className="mb-16 md:mb-24">
            <div className="bg-gradient-to-br from-navy via-[#1A3A5C] to-[#0D2137] rounded-3xl p-8 md:p-12 lg:p-16 overflow-hidden relative">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
              </div>
              <div className="relative">
                <div className="text-center mb-12">
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="w-12 h-px bg-gold"></div>
                    <span className="text-gold text-sm font-medium tracking-widest uppercase">
                      {isRTL ? 'لماذا تختارنا' : 'Why Choose Us'}
                    </span>
                    <div className="w-12 h-px bg-gold"></div>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
                    {isRTL ? 'لماذا تشارك' : 'Why Partner'} <span className="text-gold">{isRTL ? 'مع الأبل؟' : 'With ALEBEL?'}</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                  {whyPartner.map((item, index) => (
                    <div key={index} className="group">
                      <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 h-full border border-white/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-500">
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-gold/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                          <i className={`${item.icon} text-xl md:text-2xl text-gold`}></i>
                        </div>
                        <h3 className="text-lg md:text-xl font-semibold text-white mb-3">{t(item.titleKey)}</h3>
                        <p className="text-white/70 leading-relaxed text-xs md:text-sm">{t(item.descKey)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Guiding Principles */}
          <div>
            <div className="text-center mb-12 md:mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-px bg-gold"></div>
                <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('aboutPage.values.label')}</span>
                <div className="w-12 h-px bg-gold"></div>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-navy">
                {t('aboutPage.values.title1')} <span className="text-gold">{t('aboutPage.values.title2')}</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {values.map((value, index) => (
                <div key={index} className={`group ${index === 4 ? 'lg:col-start-2' : ''}`}>
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 h-full border border-gray-100 hover:border-gold/30 hover:shadow-xl transition-all duration-500">
                    <div className="w-12 h-12 md:w-14 md:h-14 animated-gradient-box rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${value.icon} text-xl md:text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-navy mb-3">{t(value.titleKey)}</h3>
                    <p className="text-gray-600 leading-relaxed text-xs md:text-sm">{t(value.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
